import { Box, Button, IconButton, Typography } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getCurrMonth, getMMMMYYYYFromString, getYYYYMFromString } from '../../shared/functions/dateProcessor';
interface MonthSelectorProps {
  disabled: boolean;
  currMonth: string;
  onMonthChange: (month: string) => void;
  showTodayButton?: boolean;
  showContainerCard?: boolean;
  containerSx?: SxProps<Theme>;
  labelContainerSx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
  buttonSx?: SxProps<Theme>;
}

export default function MonthSelector(props: Readonly<MonthSelectorProps>) {
  const {
    disabled,
    currMonth,
    onMonthChange,
    showTodayButton = true,
    showContainerCard = true,
    containerSx,
    labelContainerSx,
    labelSx,
    buttonSx,
  } = props;
  const theme = useTheme();

  const handleClickNextMonth = () => {
    const nextMonth = getYYYYMFromString(currMonth, 1);
    onMonthChange(nextMonth);
  };

  const handleClickPrevMonth = () => {
    const prevMonth = getYYYYMFromString(currMonth, -1);
    onMonthChange(prevMonth);
  };

  const handleClickToday = () => {
    onMonthChange(getCurrMonth());
  };

  const monthLabel = disabled ? 'Filter View' : getMMMMYYYYFromString(currMonth);

  return (
    <Box
      sx={[{ display: 'flex', alignItems: 'center', gap: 1 }, ...(containerSx ? [containerSx] : [])] as SxProps<Theme>}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          border: showContainerCard ? `1px solid ${theme.palette.whiteScaleDark.main}` : 'none',
          borderRadius: showContainerCard ? '8px' : 0,
          backgroundColor: showContainerCard ? theme.palette.common.white : 'transparent',
          overflow: showContainerCard ? 'hidden' : 'visible',
          minHeight: '36px',
        }}
      >
        <IconButton
          disabled={disabled}
          sx={
            [
              {
                borderRadius: showContainerCard ? 0 : '8px',
                padding: '6px',
                color: '#111111',
                '&:hover': {
                  backgroundColor: theme.palette.whiteScaleDark.main,
                },
              },
              ...(buttonSx ? [buttonSx] : []),
            ] as SxProps<Theme>
          }
          size="small"
          onClick={() => handleClickPrevMonth()}
          data-cy="tracker-table-month-selector-prev-month"
          aria-label="Previous month"
        >
          <ChevronLeftIcon sx={{ fontSize: 22 }} />
        </IconButton>
        <Box
          sx={
            [
              {
                minWidth: '148px',
                px: 1.5,
                textAlign: 'center',
              },
              ...(labelContainerSx ? [labelContainerSx] : []),
            ] as SxProps<Theme>
          }
        >
          <Typography
            variant="h6"
            sx={
              [
                ...(labelSx ? [labelSx] : []),
                {
                  fontFamily: '"Open Sans", Arial, sans-serif',
                  fontSize: '18px',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  letterSpacing: 'normal',
                  color: '#111111',
                },
              ] as SxProps<Theme>
            }
          >
            {monthLabel}
          </Typography>
        </Box>
        <IconButton
          disabled={disabled}
          sx={
            [
              {
                borderRadius: showContainerCard ? 0 : '8px',
                padding: '6px',
                color: '#111111',
                '&:hover': {
                  backgroundColor: theme.palette.whiteScaleDark.main,
                },
              },
              ...(buttonSx ? [buttonSx] : []),
            ] as SxProps<Theme>
          }
          size="small"
          onClick={() => handleClickNextMonth()}
          data-cy="tracker-table-month-selector-next-month"
          aria-label="Next month"
        >
          <ChevronRightIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>
      {showTodayButton ? (
        <Button
          disabled={disabled}
          onClick={handleClickToday}
          aria-label="Today"
          size="small"
          variant="outlined"
          sx={{
            borderRadius: '8px',
            border: `1px solid ${theme.palette.whiteScaleDark.main}`,
            px: 1.25,
            minHeight: '36px',
            fontFamily: '"Open Sans", Arial, sans-serif',
            fontSize: '0.9rem',
            fontWeight: 500,
            lineHeight: 1.1,
            textTransform: 'none',
            color: '#111111',
            '&:hover': {
              backgroundColor: theme.palette.whiteScaleDark.main,
              border: `1px solid ${theme.palette.whiteScaleDark.main}`,
            },
          }}
        >
          Today
        </Button>
      ) : null}
    </Box>
  );
}
